import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Bot, 
  Send, 
  Mic, 
  MicOff, 
  Upload, 
  Monitor, 
  ChevronRight, 
  ChevronLeft,
  Paperclip,
  X,
  Loader2
} from 'lucide-react'
import { Button } from './ui/button'
import { Textarea } from './ui/textarea'
import { ScrollArea } from './ui/scroll-area'
import { Card } from './ui/card'
import { cn } from '@/lib/utils'
import { createPortal } from 'react-dom'
import { chatGenerate, deepResearch } from '@/services/emailAI'
import Markdown from '@/components/Markdown'
import { streamChat } from '@/services/emailAI'
// html2canvas disabled in this build environment to avoid bundling issues
import { toast } from 'sonner'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  context?: {
    highlightedText?: string
    cursorPosition?: { x: number; y: number }
    pageContext?: string
  }
}

interface ResizableAIChatProps {
  onWidthChange?: (width: number) => void
  onCollapseChange?: (collapsed: boolean) => void
}

export function ResizableAIChat({ onWidthChange, onCollapseChange }: ResizableAIChatProps = {}) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [width, setWidth] = useState(400)
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: 'Hello! I\'m your AI assistant. I can help you manage emails, schedule meetings, and automate workflows. Try highlighting text anywhere on the page - I\'ll see it!',
      timestamp: new Date()
    }
  ])
  const [input, setInput] = useState('')
  const [isListening, setIsListening] = useState(false)
  const [isScreenSharing, setIsScreenSharing] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [highlightedText, setHighlightedText] = useState('')
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 })
  const [isTyping, setIsTyping] = useState(false)
  const [screenCapture, setScreenCapture] = useState<string | null>(null)
  const [isCapturing, setIsCapturing] = useState(false)
  const [useWebSearch, setUseWebSearch] = useState(false)
  const [useDeepResearch, setUseDeepResearch] = useState(false)
  
  const resizeRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isDragging = useRef(false)
  const endRef = useRef<HTMLDivElement | null>(null)
  const [streamingId, setStreamingId] = useState<string | null>(null)

  // Track mouse position and highlights globally
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setCursorPosition({ x: e.clientX, y: e.clientY })
    }

    const handleSelection = () => {
      const selection = window.getSelection()
      const text = selection?.toString().trim()
      if (text) {
        setHighlightedText(text)
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleSelection)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleSelection)
    }
  }, [])

  // Notify parent of width changes
  useEffect(() => {
    if (onWidthChange) {
      onWidthChange(width)
    }
  }, [width, onWidthChange])

  // Notify parent of collapse changes
  useEffect(() => {
    if (onCollapseChange) {
      onCollapseChange(isCollapsed)
    }
  }, [isCollapsed, onCollapseChange])

  // Handle resize
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging.current) {
        const newWidth = window.innerWidth - e.clientX
        setWidth(Math.max(300, Math.min(800, newWidth)))
      }
    }

    const handleMouseUp = () => {
      isDragging.current = false
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  const handleMouseDown = () => {
    isDragging.current = true
  }

  const handleSend = async () => {
    if (!input.trim() && selectedFiles.length === 0) return

    const newMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
      context: {
        highlightedText: highlightedText || undefined,
        cursorPosition,
        pageContext: window.location.pathname
      }
    }

    setMessages(prev => [...prev, newMessage])
    
    // Build enhanced prompt with screen capture context
    const contextParts = [
      input,
      highlightedText ? `\n\nHighlighted text: "${highlightedText}"` : '',
      `\n\nPage: ${window.location.pathname}`,
      isScreenSharing && screenCapture ? '\n\n[Screen capture is available - the AI can see the current page layout and content]' : ''
    ]
    
    // Extract text content from the page for better context
    if (isScreenSharing) {
      const mainContent = document.querySelector('main')
      if (mainContent) {
        const textContent = mainContent.textContent?.substring(0, 1000) || ''
        if (textContent.trim()) {
          contextParts.push(`\n\nVisible page content: ${textContent.trim()}...`)
        }
      }
    }
    
    const prompt = contextParts.join('')

    setInput('')
    setHighlightedText('')
    setIsTyping(true)
    const assistantId = (Date.now() + 1).toString()
    setMessages(prev => [...prev, { id: assistantId, role: 'assistant', content: '', timestamp: new Date() }])
    setStreamingId(assistantId)
    
    // UNIFIED ARCHITECTURE: Route ALL queries through gpt5NanoAgent
    // This ensures consistent API calls, tracing, and agentic logic with AI Task Assistant
    try {
      const { executeUserIntent } = await import('@/services/gpt5NanoAgent');
      
      // Visual indicator that unified system is active
      setMessages(prev => prev.map(m => m.id === assistantId ? { 
        ...m, 
        content: '🔧 **Unified Agent System Active**\n\nAnalyzing your request and selecting appropriate tools...\n\n' 
      } : m))
      
      // Execute through unified agent (same as AI Task Assistant on the left)
      const result = await executeUserIntent(prompt);
      
      let responseText = '';
      
      if (result.success && result.execution_result) {
        const executionResult = result.execution_result;
        
        // Format response based on action type
        if (executionResult.action === 'email_send') {
          responseText = `✅ **Email Sent Successfully**\n\nTo: ${executionResult.recipients.join(', ')}\nMessage ID: ${executionResult.messageId}\nTimestamp: ${new Date(executionResult.timestamp).toLocaleString()}\n\nThe email was sent via Microsoft Graph API using your connected account.`;
        } else if (executionResult.action === 'email_search') {
          responseText = `🔍 **Email Search Results**\n\n**Query:** "${executionResult.query}"\n**Found:** ${executionResult.resultsCount} email(s)\n\n`;
          
          if (executionResult.results && executionResult.results.length > 0) {
            executionResult.results.slice(0, 10).forEach((email: any, idx: number) => {
              responseText += `${idx + 1}. **${email.subject || '(No Subject)'}**\n`;
              responseText += `   From: ${email.senderName || email.senderEmail}\n`;
              responseText += `   Date: ${new Date(email.receivedDate).toLocaleString()}\n`;
              if (email.snippet) {
                responseText += `   ${email.snippet.substring(0, 100)}...\n`;
              }
              responseText += `\n`;
            });
          } else {
            responseText += `No emails found matching your criteria.\n`;
          }
        } else if (executionResult.action === 'get_recent_emails') {
          responseText = `📧 **Recent Emails**\n\n**Timeframe:** ${executionResult.timeframe}\n**Found:** ${executionResult.resultsCount} email(s)\n\n`;
          
          if (executionResult.results && executionResult.results.length > 0) {
            executionResult.results.slice(0, 10).forEach((email: any, idx: number) => {
              responseText += `${idx + 1}. **${email.subject || '(No Subject)'}**\n`;
              responseText += `   From: ${email.senderName || email.senderEmail}\n`;
              responseText += `   Date: ${new Date(email.receivedDate).toLocaleString()}\n`;
              if (email.snippet) {
                responseText += `   ${email.snippet.substring(0, 100)}...\n`;
              }
              responseText += `\n`;
            });
          }
        } else if (executionResult.action === 'answer_question') {
          responseText = executionResult.answer || executionResult.information || result.agent_response?.reasoning || 'Unable to answer based on email context.';
        } else {
          // Generic success response
          responseText = `✅ **Action Completed**\n\n${JSON.stringify(executionResult, null, 2)}`;
        }
        
        // Add trace information if available
        if (result.trace) {
          responseText += `\n\n---\n\n**🔍 API Trace:**\n`;
          result.trace.forEach((entry: any) => {
            responseText += `\n• ${entry.api_name}: ${entry.status} (${entry.duration_ms}ms)`;
          });
        }
      } else {
        responseText = `❌ **Error processing request**\n\n${result.error || 'Unknown error occurred'}`;
        if (result.agent_response) {
          responseText += `\n\n**Agent Response:**\n${result.agent_response.thinking}`;
        }
      }
      
      setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: responseText } : m))
      setIsTyping(false)
      setStreamingId(null)
      setUseWebSearch(false)
      setUseDeepResearch(false)
      
    } catch (error) {
      console.error('Error executing unified agent:', error);
      
      // Fallback to old streaming method if unified system fails
      console.log('Falling back to streaming chat...');
      
      setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: '' } : m))
      
      // Determine if we should use orchestration (for complex research queries)
      const useOrchestration = useDeepResearch || prompt.toLowerCase().includes('research') || 
                               prompt.toLowerCase().includes('analyze') || 
                               prompt.toLowerCase().includes('compare');
      
      // Adaptive token limits based on query complexity and length
      const baseTokens = useOrchestration ? 2500 : 800;
      const promptComplexity = Math.min(1500, Math.floor(prompt.length / 4));
      const adaptiveMaxTokens = Math.min(4000, baseTokens + promptComplexity);
      
      const controller = streamChat({
        prompt,
        maxTokens: adaptiveMaxTokens,
        useWebSearch: useWebSearch || useOrchestration,
        useOrchestration,
        onChunk: (chunk) => {
          setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: (m.content || '') + chunk } : m))
          endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
        },
        onDone: (finalText) => {
          setIsTyping(false)
          setStreamingId(null)
          setUseWebSearch(false)
          setUseDeepResearch(false)
          if (!finalText) {
            setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: getContextualResponse(newMessage) } : m))
          }
        }
      })
    }
  }

  const getContextualResponse = (msg: Message): string => {
    const responses = [
      'I understand. Based on the highlighted text and your current context, here\'s what I can help with...',
      'Great question! I can see you\'re on the ' + msg.context?.pageContext + ' page. Let me assist you with that.',
      'I\'ve analyzed your selection and the cursor position. Here\'s my recommendation...',
      'Working on that for you now. I have access to your email, calendar, and workflow systems.'
    ]
    return responses[Math.floor(Math.random() * responses.length)]
  }

  const toggleVoiceChat = () => {
    setIsListening(!isListening)
    // Mock voice recognition
    if (!isListening) {
      setTimeout(() => {
        setInput(prev => prev + ' [Voice input: "Schedule meeting for tomorrow"]')
      }, 2000)
    }
  }

  const captureScreen = async () => {
    setIsCapturing(true)
    try {
      // Use native browser screen capture API instead of html2canvas
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      if (!ctx) {
        toast.error('Canvas context not available')
        setIsScreenSharing(false)
        return
      }

      // Get the main content area
      const mainElement = document.querySelector('main') || document.body
      const rect = mainElement.getBoundingClientRect()
      
      // Set canvas dimensions
      canvas.width = Math.min(rect.width, 1920)
      canvas.height = Math.min(rect.height, 1080)
      
      // Create a simple visual representation
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      
      // Draw text representation of the page
      ctx.fillStyle = '#000000'
      ctx.font = '16px Inter, sans-serif'
      ctx.fillText(`Page: ${window.location.pathname}`, 20, 40)
      ctx.fillText(`Time: ${new Date().toLocaleString()}`, 20, 70)
      
      // Get visible text content
      const textContent = mainElement.textContent?.substring(0, 500) || 'No content'
      const lines = textContent.match(/.{1,80}/g) || []
      lines.slice(0, 15).forEach((line, i) => {
        ctx.fillText(line.trim(), 20, 110 + (i * 25))
      })
      
      // Convert to data URL
      const dataUrl = canvas.toDataURL('image/png')
      setScreenCapture(dataUrl)
      setIsScreenSharing(true)
      
      toast.success('Screen sharing started - AI can now see your page context')
    } catch (error) {
      console.error('Screen capture error:', error)
      toast.error('Failed to capture screen')
      setIsScreenSharing(false)
      setScreenCapture(null)
    } finally {
      setIsCapturing(false)
    }
  }

  const toggleScreenShare = () => {
    if (isScreenSharing) {
      // Stop sharing
      setIsScreenSharing(false)
      setScreenCapture(null)
      toast.info('Screen sharing stopped.')
    } else {
      // Start sharing
      captureScreen()
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setSelectedFiles(prev => [...prev, ...files])
  }

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  if (isCollapsed) {
    return createPortal(
      <motion.div
        initial={{ x: 48 }}
        animate={{ x: 0 }}
        exit={{ x: 48 }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className="fixed right-0 inset-y-0 w-12 z-[9999]"
        style={{ pointerEvents: 'auto' }}
      >
        <button
          onClick={() => setIsCollapsed(false)}
          className="h-full w-full glass-card border-l flex items-center justify-center hover:bg-primary/10 transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-foreground/60" />
        </button>
      </motion.div>,
      document.body
    )
  }

  return createPortal(
    <motion.div
      initial={{ x: width }}
      animate={{ x: 0 }}
      exit={{ x: width }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
      style={{ width, pointerEvents: 'auto' }}
      className="fixed right-0 inset-y-0 z-[9999] bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/60 border-l border-border/50 flex flex-col shadow-lg"
    >
      {/* Resize handle */}
      <div
        ref={resizeRef}
        onMouseDown={handleMouseDown}
        className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-primary/50 transition-colors"
      />

      {/* Header */}
      <div className="p-4 border-b border-border/50 flex items-center justify-between backdrop-blur-sm bg-card/80">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Bot className="w-5 h-5 text-primary" />
            {(useWebSearch || useDeepResearch) && (
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-accent rounded-full animate-pulse" />
            )}
          </div>
          <div>
            <h3 className="font-medium text-sm">AI Assistant</h3>
            <p className="text-xs text-muted-foreground">
              {useDeepResearch ? 'GPT-5 Deep Research' : useWebSearch ? 'GPT-4-mini + Web' : 'GPT-5 Powered'}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsCollapsed(true)}
          className="h-8 w-8 p-0"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Context indicators */}
      <AnimatePresence>
        {highlightedText && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="px-4 py-2 bg-primary/10 border-b border-primary/20"
          >
            <p className="text-xs text-muted-foreground mb-1">Selected text:</p>
            <p className="text-sm font-medium truncate">{highlightedText}</p>
          </motion.div>
        )}
        {isScreenSharing && screenCapture && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="px-4 py-2 bg-accent/10 border-b border-accent/20"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Monitor className="w-4 h-4 text-accent" />
                <div>
                  <p className="text-xs text-muted-foreground">Screen sharing active</p>
                  <p className="text-xs font-medium">AI can see your current view</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsScreenSharing(false)
                  setScreenCapture(null)
                }}
                className="h-6 text-xs"
              >
                Stop
              </Button>
            </div>
            {screenCapture && (
              <div className="mt-2 rounded overflow-hidden border border-accent/20">
                <img 
                  src={screenCapture} 
                  alt="Screen capture" 
                  className="w-full h-20 object-cover object-top"
                />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.map(message => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={cn(
                'rounded-lg p-3 max-w-[80%] transition-all duration-200',
                message.role === 'user'
                  ? 'bg-primary text-primary-foreground shadow-md hover:shadow-lg ml-auto'
                  : 'glass-card hover:shadow-xl'
              )}
            >
              {message.role === 'assistant' ? (
                <Markdown content={message.content} className="prose prose-zinc dark:prose-invert max-w-none text-sm" />
              ) : (
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              )}
              {message.context?.highlightedText && (
                <div className="mt-2 pt-2 border-t border-border/20">
                  <p className="text-xs opacity-70">
                    Context: "{message.context.highlightedText}"
                  </p>
                </div>
              )}
            </motion.div>
          ))}
          <div ref={endRef} />
          {isTyping && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="glass-card rounded-lg p-3 w-fit"
            >
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </motion.div>
          )}
        </div>
      </ScrollArea>

      {/* File attachments */}
      <AnimatePresence>
        {selectedFiles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="px-4 py-2 border-t border-border/50"
          >
            <div className="flex flex-wrap gap-2">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-secondary/50 rounded-lg px-3 py-1.5"
                >
                  <Paperclip className="w-3 h-3" />
                  <span className="text-xs truncate max-w-[120px]">{file.name}</span>
                  <button
                    onClick={() => removeFile(index)}
                    className="hover:text-destructive transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input area */}
      <div className="p-4 border-t border-border/50 space-y-3">
        {/* AI Mode Selection */}
        <div className="flex gap-2 items-center justify-between text-xs">
          <div className="flex gap-2">
            <button
              onClick={() => {
                setUseWebSearch(!useWebSearch);
                setUseDeepResearch(false);
              }}
              className={cn(
                'px-2 py-1 rounded-md transition-colors',
                useWebSearch ? 'bg-accent text-accent-foreground' : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'
              )}
            >
              🌐 Web Search
            </button>
            <button
              onClick={() => {
                setUseDeepResearch(!useDeepResearch);
                setUseWebSearch(false);
              }}
              className={cn(
                'px-2 py-1 rounded-md transition-colors',
                useDeepResearch ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'
              )}
            >
              🔬 Deep Research
            </button>
          </div>
          <span className="text-muted-foreground">
            {useDeepResearch ? 'Multi-step GPT-5 analysis' : useWebSearch ? 'Real-time web data' : 'Standard mode'}
          </span>
        </div>
        
        {/* Action buttons */}
        <div className="flex gap-2">
          <Button
            variant={isScreenSharing ? 'default' : 'outline'}
            size="sm"
            onClick={toggleScreenShare}
            disabled={isCapturing}
            className="flex-1"
          >
            {isCapturing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Monitor className="w-4 h-4 mr-2" />
            )}
            {isCapturing ? 'Capturing...' : isScreenSharing ? 'Sharing' : 'Screen'}
          </Button>
          <Button
            variant={isListening ? 'default' : 'outline'}
            size="sm"
            onClick={toggleVoiceChat}
            className="flex-1"
          >
            {isListening ? <MicOff className="w-4 h-4 mr-2" /> : <Mic className="w-4 h-4 mr-2" />}
            {isListening ? 'Stop' : 'Voice'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            className="flex-1"
          >
            <Upload className="w-4 h-4 mr-2" />
            File
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {/* Text input */}
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSend()
              }
            }}
            placeholder={
              useDeepResearch 
                ? "Deep research mode: Ask complex questions requiring multi-source analysis..." 
                : useWebSearch
                  ? "Web search enabled: Ask about current events, latest info..."
                  : "Ask me anything... I can see what you highlight!"
            }
            className="min-h-[60px] resize-none"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() && selectedFiles.length === 0}
            className="h-auto"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>

        {/* Cursor position indicator */}
        <div className="text-xs text-muted-foreground text-center">
          Cursor: ({cursorPosition.x}, {cursorPosition.y}) • Page: {window.location.pathname}
        </div>
      </div>
    </motion.div>,
    document.body
  )
}